import { useControlBoard, useMarkNotificationRead, useNotifications } from "@/api/board/useBoard";
import type { BoardCase } from "@/api/board/boardCaseFields";
import type { PendingHandover } from "@/api/handover/HandoverApi";
import {
  useAcceptHandover,
  useCancelHandover,
  usePendingHandovers,
  useRejectHandover,
} from "@/api/handover/useHandover";
import { useAuth } from "@/auth/AuthContext";
import { routes } from "@/constants/routes";
import { labelOf, NOTIFICATION_CATEGORY_LABEL } from "@/constants/uiLabels";
import { OfflineConflictPanel } from "@/offline/OfflineConflictPanel";
import { useTheme } from "@/theme/ThemeProvider";
import { AppButton } from "@/ui/components/AppButton";
import { DenseRow } from "@/ui/components/DenseRow";
import { EmptyState } from "@/ui/components/EmptyState";
import { HeaderAction } from "@/ui/components/HeaderAction";
import { Screen } from "@/ui/components/Screen";
import { SectionCard } from "@/ui/components/SectionCard";
import { CaseTable } from "@/ui/components/CaseTable";
import { SummaryTile } from "@/ui/components/SummaryTile";
import {
  shownUnreadCount,
  visibleBoardNotifications,
  type BoardNoticeRow,
} from "@/ui/board/boardNotifications";
import {
  BOARD_KPI_LABEL,
  boardKpiCounts,
  formatCaseCount,
  type BoardKpiKey,
} from "@/ui/board/caseTableModel";
import { kpiTileFlex } from "@/ui/board/kpiBarLayout";
import { formatRelativeDayDE, formatTimeDE, formatTimestampDE, uiErrorMessage } from "@/utils/isoDate";
import { useRouter } from "expo-router";
import { useEffect, useMemo, useRef, useState } from "react";
import { Pressable, Text, TextInput, useWindowDimensions, View } from "react-native";

const NOTE_PREVIEW = 3;
const KPI_KEYS: readonly BoardKpiKey[] = ["due", "overdue", "missing", "active"];

function formatNoteTime(iso: string): string {
  const day = formatRelativeDayDE(iso);
  if (day === "Heute") return formatTimeDE(iso);
  return day;
}

function handoverHeading(number: string | null, name: string | null): string {
  const num = number?.trim() ?? "";
  const who = name?.trim() ?? "";
  if (num && who) return `${num} · ${who}`;
  return num || who || "Tier";
}

function siteNames(cases: readonly BoardCase[]): Map<string, string> {
  const names = new Map<string, string>();
  for (const row of cases) {
    const id = row.current_care_site_id;
    const name = row.current_care_site_name?.trim() ?? "";
    if (id && name) names.set(id, name);
  }
  return names;
}

const HANDOVER_ALREADY = "Diese Übergabe wurde bereits bearbeitet.";
const HANDOVER_FAILED = "Die Übergabe konnte nicht gespeichert werden.";

/**
 * Ton nur bei Handlungsdruck. Überfällig bleibt warning wie der Tabellen-Chip,
 * Medikament fehlt ist danger, Heute fällig bleibt neutral.
 */
function kpiTone(key: BoardKpiKey): "warning" | "danger" | "neutral" {
  if (key === "missing") return "danger";
  if (key === "overdue") return "warning";
  return "neutral";
}

/**
 * Control Board: dichte KPI-Leiste, Übergaben, Pflegefälle, darunter Meldungen.
 */
export default function HomeScreen() {
  const { theme } = useTheme();
  const router = useRouter();
  const { user } = useAuth();
  const { width: windowWidth } = useWindowDimensions();
  const [kpiBarWidth, setKpiBarWidth] = useState<number | null>(null);
  const { board, loading, error, refetch } = useControlBoard();
  const {
    notifications,
    loading: notesLoading,
    error: notesError,
    refetch: refetchNotes,
  } = useNotifications();
  const { handovers, refetch: refetchHandovers } = usePendingHandovers();
  const acceptHandover = useAcceptHandover();
  const rejectHandover = useRejectHandover();
  const cancelHandover = useCancelHandover();
  const markRead = useMarkNotificationRead();
  const [showAllNotes, setShowAllNotes] = useState(false);
  const [visibleCaseCount, setVisibleCaseCount] = useState<number | null>(null);
  const [kpi, setKpi] = useState<BoardKpiKey | null>(null);
  const [now, setNow] = useState(() => new Date());
  const [rejectId, setRejectId] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState("");
  const [rejectError, setRejectError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<{ id: string; message: string } | null>(null);
  const [handledNotice, setHandledNotice] = useState<string | null>(null);
  const inFlight = useRef(false);

  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(timer);
  }, []);

  const boardCases = useMemo(() => board?.cases ?? [], [board?.cases]);
  const counts = useMemo(() => boardKpiCounts(boardCases, now), [boardCases, now]);
  const sites = useMemo(() => siteNames(boardCases), [boardCases]);
  const noteRows = useMemo(
    () => visibleBoardNotifications(notifications, boardCases),
    [notifications, boardCases],
  );
  const shownUnread = shownUnreadCount(noteRows);
  const noteLimit = showAllNotes ? noteRows.length : NOTE_PREVIEW;
  const visibleNotes = noteRows.slice(0, noteLimit);

  async function refreshBoard() {
    await refetch();
    await refetchNotes();
    await refetchHandovers();
  }

  function toggleKpi(key: BoardKpiKey) {
    setKpi((current) => (current === key ? null : key));
  }

  async function runHandover(handoverId: string, work: () => Promise<unknown>) {
    if (inFlight.current) return;
    inFlight.current = true;
    setBusyId(handoverId);
    setActionError((current) => (current?.id === handoverId ? null : current));
    try {
      await work();
      setHandledNotice(null);
    } catch (error) {
      const raw = error instanceof Error ? error.message : String(error ?? "");
      if (/not pending/i.test(raw)) {
        setActionError((current) => (current?.id === handoverId ? null : current));
        setHandledNotice(HANDOVER_ALREADY);
        try {
          await refetchHandovers();
        } catch {
          // Die Meldung bleibt am Block, auch wenn die Liste nicht nachlädt.
        }
      } else {
        setActionError({ id: handoverId, message: HANDOVER_FAILED });
      }
    } finally {
      inFlight.current = false;
      setBusyId(null);
    }
  }

  return (
    <Screen
      title="Control Board"
      titleAction={
        <AppButton
          variant="ghost"
          accessibilityRole="button"
          accessibilityLabel="Aktualisieren"
          style={{ minHeight: theme.control.heightTouch }}
          onPress={() => {
            void refreshBoard();
          }}
        >
          Aktualisieren
        </AppButton>
      }
      scroll
    >
      <View
        testID="board-scroll-body"
        style={{ gap: theme.space.md }}
      >
        <OfflineConflictPanel />

        {board?.mode === "care_board" ? (
          <View
            testID="board-kpi-bar"
            onLayout={(event) => {
              const next = event.nativeEvent.layout.width;
              setKpiBarWidth((current) =>
                current != null && Math.abs(current - next) < 0.5 ? current : next,
              );
            }}
            style={{
              flexDirection: "row",
              flexWrap: "wrap",
              alignItems: "flex-start",
              alignContent: "flex-start",
              gap: theme.space.xs,
              width: "100%",
              maxWidth: "100%",
            }}
          >
            {KPI_KEYS.map((key) => (
              <SummaryTile
                key={key}
                dense
                label={BOARD_KPI_LABEL[key]}
                value={counts[key]}
                tone={kpiTone(key)}
                selected={kpi === key}
                onPress={() => toggleKpi(key)}
                style={kpiTileFlex(kpiBarWidth ?? Math.max(0, windowWidth - theme.space.lg * 2))}
              />
            ))}
          </View>
        ) : null}

        {handovers.length > 0 || handledNotice ? (
          <SectionCard title={handovers.length > 0 ? `Übergaben (${handovers.length})` : "Übergaben"}>
            <View testID="handover-block">
              {handledNotice ? <HandoverAlert message={handledNotice} /> : null}
              {handovers.map((handover) => (
                <HandoverRow
                  key={handover.id}
                  handover={handover}
                  siteName={sites.get(handover.from_care_site_id) ?? null}
                  sender={!!user?.id && handover.initiated_by === user.id}
                  busy={busyId != null}
                  rejecting={rejectId === handover.id}
                  reason={rejectId === handover.id ? rejectReason : ""}
                  reasonError={rejectId === handover.id ? rejectError : null}
                  actionError={actionError?.id === handover.id ? actionError.message : null}
                  onOpen={() => router.push(routes.myAnimal(handover.case_id) as never)}
                  onAccept={() =>
                    runHandover(handover.id, async () => {
                      await acceptHandover.mutateAsync({ handoverId: handover.id });
                      await Promise.all([refetchHandovers(), refetch()]);
                    })
                  }
                  onRejectPress={() => {
                    if (inFlight.current) return;
                    if (rejectId !== handover.id) {
                      setRejectId(handover.id);
                      setRejectReason("");
                      setRejectError(null);
                      return;
                    }
                    const reason = rejectReason.trim();
                    if (!reason) {
                      setRejectError("Grund angeben.");
                      return;
                    }
                    void runHandover(handover.id, async () => {
                      await rejectHandover.mutateAsync({ handoverId: handover.id, reason });
                      setRejectId(null);
                      setRejectReason("");
                      setRejectError(null);
                      await refetchHandovers();
                    });
                  }}
                  onReason={setRejectReason}
                  onCancel={() =>
                    runHandover(handover.id, async () => {
                      await cancelHandover.mutateAsync({
                        handoverId: handover.id,
                        reason: "Abgebrochen",
                      });
                      await refetchHandovers();
                    })
                  }
                />
              ))}
            </View>
          </SectionCard>
        ) : null}

        {loading ? (
          <Text style={{ color: theme.color.muted }}>Lade Board…</Text>
        ) : error ? (
          <Text style={{ color: theme.color.dangerText }}>{(error as Error).message}</Text>
        ) : board?.mode === "care_board" ? (
          <SectionCard
            title="Pflegefälle"
            subtitle={formatCaseCount(visibleCaseCount ?? boardCases.length)}
            bodyStyle={{ paddingHorizontal: theme.space.sm, paddingVertical: theme.space.sm }}
          >
            <CaseTable
              cases={boardCases}
              now={now}
              kpi={kpi}
              onKpiChange={setKpi}
              onVisibleCount={setVisibleCaseCount}
              onOpenCase={(caseId) => router.push(routes.myAnimal(caseId) as never)}
              onOpenProtocol={(caseId) => router.push(routes.myAnimalProtocol(caseId) as never)}
            />
          </SectionCard>
        ) : (
          <SectionCard title="Tiergeschehen">
            <Text
              style={{
                color: theme.color.muted,
                fontSize: theme.fontSize.small,
                marginBottom: theme.space.sm,
              }}
            >
              Kein Pflegezugriff auf Fälle – hier siehst du das allgemeine
              Tiergeschehen, ohne Pflegeaufgaben oder Pläne.
            </Text>
            {(board?.feed ?? []).length === 0 ? (
              <EmptyState title="Noch keine Ereignisse." />
            ) : (
              <View>
                {(board?.feed ?? []).map((item, idx) => (
                  <DenseRow
                    key={`${item.case_id}-${item.kind}-${idx}`}
                    title={item.label}
                    meta={`${item.case_number ?? ""} ${item.name ?? ""} · ${formatTimestampDE(
                      item.occurred_at
                    )}`}
                    onPress={() => router.push(routes.myAnimal(item.case_id) as never)}
                  />
                ))}
              </View>
            )}
            <View style={{ marginTop: theme.space.sm }}>
              <HeaderAction
                label="Zur Tierliste"
                onPress={() => router.push(routes.myAnimals as never)}
              />
            </View>
          </SectionCard>
        )}

        <SectionCard
          title="Benachrichtigungen"
          subtitle={shownUnread ? `${shownUnread} ungelesen` : undefined}
          bodyStyle={{ paddingHorizontal: 0, paddingVertical: 0 }}
        >
          {notesLoading && notifications.length === 0 ? (
            <View style={{ paddingHorizontal: theme.space.md, paddingVertical: theme.space.sm }}>
              <Text style={{ color: theme.color.muted }}>Laden…</Text>
            </View>
          ) : notesError && notifications.length === 0 ? (
            <View style={{ paddingHorizontal: theme.space.md, paddingVertical: theme.space.sm }}>
              <Text style={{ color: theme.color.dangerText }}>
                {uiErrorMessage(notesError, "Benachrichtigungen konnten nicht geladen werden.")}
              </Text>
            </View>
          ) : noteRows.length === 0 ? (
            <View style={{ paddingHorizontal: theme.space.md }}>
              <EmptyState title="Keine In-App-Meldungen." />
            </View>
          ) : (
            <View>
              {notesError ? (
                <Text
                  style={{
                    color: theme.color.dangerText,
                    paddingHorizontal: theme.space.md,
                    paddingVertical: theme.space.sm,
                  }}
                >
                  {uiErrorMessage(notesError, "Benachrichtigungen konnten nicht geladen werden.")}
                </Text>
              ) : null}
              {visibleNotes.map((row) => (
                <NoticeRow
                  key={row.id}
                  row={row}
                  onPress={async () => {
                    if (row.kind === "summary") {
                      await Promise.all(
                        row.notes
                          .filter((note) => !note.read_at)
                          .map((note) => markRead.mutateAsync(note.id)),
                      );
                      router.push(routes.myAnimal(row.caseId) as never);
                      return;
                    }
                    if (!row.note.read_at) await markRead.mutateAsync(row.note.id);
                    if (row.note.entity_id) {
                      router.push(routes.myAnimal(row.note.entity_id) as never);
                    }
                  }}
                />
              ))}
              {noteRows.length > NOTE_PREVIEW ? (
                <Pressable
                  accessibilityRole="button"
                  accessibilityLabel={
                    showAllNotes ? "Weniger anzeigen" : `Alle anzeigen (${noteRows.length})`
                  }
                  onPress={() => setShowAllNotes((value) => !value)}
                  style={{
                    minHeight: theme.control.heightTouch,
                    justifyContent: "center",
                    paddingVertical: theme.space.sm,
                    paddingHorizontal: theme.space.md,
                  }}
                >
                  <Text
                    style={{
                      color: theme.color.primary,
                      fontWeight: "700",
                      fontSize: theme.fontSize.small,
                    }}
                  >
                    {showAllNotes ? "Weniger anzeigen" : `Alle anzeigen (${noteRows.length})`}
                  </Text>
                </Pressable>
              ) : null}
            </View>
          )}
        </SectionCard>
      </View>
    </Screen>
  );
}

function NoticeRow({ row, onPress }: { row: BoardNoticeRow; onPress: () => void }) {
  const { theme } = useTheme();
  const unread =
    row.kind === "summary" ? row.notes.some((note) => !note.read_at) : !row.note.read_at;
  const title = row.kind === "summary" ? row.title : row.note.title;
  const when =
    row.kind === "summary" ? row.notes[0]?.created_at ?? "" : row.note.created_at;
  return (
    <DenseRow
      emphasized={unread}
      title={title}
      style={{ backgroundColor: theme.color.surface }}
      meta={
        row.kind === "summary" ? undefined : (
          <View
            style={{
              flexDirection: "row",
              alignItems: "center",
              gap: theme.space.sm,
              flexWrap: "wrap",
            }}
          >
            <Text
              style={{
                color: theme.color.muted,
                fontSize: theme.fontSize.tiny,
                fontWeight: "700",
                textTransform: "uppercase",
                letterSpacing: 0.3,
              }}
            >
              {labelOf(NOTIFICATION_CATEGORY_LABEL, row.note.category)}
            </Text>
            {row.note.body ? (
              <Text
                style={{
                  color: theme.color.textSecondary,
                  fontSize: theme.fontSize.tiny,
                  flexShrink: 1,
                }}
                numberOfLines={1}
              >
                {row.note.body}
              </Text>
            ) : null}
          </View>
        )
      }
      leading={
        <View
          style={{
            width: 7,
            height: 7,
            borderRadius: 4,
            backgroundColor: unread ? theme.color.primary : theme.color.border,
          }}
        />
      }
      trailing={
        when ? (
          <Text
            style={{
              color: theme.color.muted,
              fontSize: theme.fontSize.tiny,
              fontVariant: ["tabular-nums"],
            }}
          >
            {formatNoteTime(when)}
          </Text>
        ) : null
      }
      onPress={() => {
        void onPress();
      }}
    />
  );
}

function HandoverAlert({ message }: { message: string }) {
  const { theme } = useTheme();
  return (
    <Text
      accessibilityRole="alert"
      accessibilityLiveRegion="assertive"
      role="alert"
      style={{ color: theme.color.dangerText, fontSize: theme.fontSize.small }}
    >
      {message}
    </Text>
  );
}

function HandoverRow({
  handover,
  siteName,
  sender,
  busy,
  rejecting,
  reason,
  reasonError,
  actionError,
  onOpen,
  onAccept,
  onRejectPress,
  onReason,
  onCancel,
}: {
  handover: PendingHandover;
  siteName: string | null;
  sender: boolean;
  busy: boolean;
  rejecting: boolean;
  reason: string;
  reasonError: string | null;
  actionError: string | null;
  onOpen: () => void;
  onAccept: () => Promise<void>;
  onRejectPress: () => void;
  onReason: (value: string) => void;
  onCancel: () => Promise<void>;
}) {
  const { theme } = useTheme();
  const reasonInput = useRef<TextInput>(null);
  useEffect(() => {
    if (!rejecting) return;
    const frame = requestAnimationFrame(() => {
      reasonInput.current?.focus();
    });
    return () => cancelAnimationFrame(frame);
  }, [rejecting]);
  const meta = [
    siteName ? `von ${siteName}` : null,
    formatTimestampDE(handover.initiated_at),
    handover.note?.trim() ? `Notiz: ${handover.note.trim()}` : null,
  ]
    .filter((part): part is string => !!part)
    .join(" · ");
  const touch = { minHeight: theme.control.heightTouch, flexShrink: 1 };
  return (
    <DenseRow title={handoverHeading(handover.case_number, handover.animal_name)} meta={meta}>
      <View
        testID="handover-actions"
        style={{
          flexDirection: "row",
          flexWrap: "wrap",
          alignItems: "center",
          gap: theme.space.xs,
          marginTop: theme.space.xs,
          width: "100%",
          maxWidth: "100%",
        }}
      >
        <AppButton
          onPress={onOpen}
          variant="outline"
          accessibilityRole="button"
          accessibilityLabel="Akte"
          style={touch}
        >
          Akte
        </AppButton>
        {sender ? (
          <AppButton
            variant="ghost"
            disabled={busy}
            accessibilityRole="button"
            accessibilityLabel="Abbrechen"
            onPress={() => {
              if (busy) return;
              void onCancel();
            }}
            style={touch}
          >
            Abbrechen
          </AppButton>
        ) : (
          <>
            <AppButton
              disabled={busy}
              accessibilityRole="button"
              accessibilityLabel="Annehmen"
              onPress={() => {
                if (busy) return;
                void onAccept();
              }}
              style={touch}
            >
              Annehmen
            </AppButton>
            <AppButton
              variant="ghost"
              disabled={busy}
              accessibilityRole="button"
              accessibilityLabel="Ablehnen"
              onPress={() => {
                if (busy) return;
                onRejectPress();
              }}
              style={touch}
            >
              Ablehnen
            </AppButton>
          </>
        )}
      </View>
      {actionError ? <HandoverAlert message={actionError} /> : null}
      {rejecting ? (
        <View style={{ width: "100%", maxWidth: "100%", marginTop: theme.space.xs, gap: theme.space.xs }}>
          <TextInput
            ref={reasonInput}
            value={reason}
            onChangeText={onReason}
            accessibilityLabel="Grund"
            placeholder="Grund"
            placeholderTextColor={theme.color.muted}
            style={{
              width: "100%",
              minHeight: theme.control.heightTouch,
              borderWidth: 1,
              borderColor: theme.color.inputBorder,
              borderRadius: theme.radius.control,
              color: theme.color.text,
              paddingHorizontal: theme.space.sm,
              fontSize: theme.fontSize.small,
            }}
          />
          {reasonError ? <HandoverAlert message={reasonError} /> : null}
        </View>
      ) : null}
    </DenseRow>
  );
}
